import { useQuilttConnector } from '@quiltt/react';
import { useAtom, useSetAtom } from 'jotai';
import { useEffect } from 'react';

import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { QUILTT_CONNECTOR_ID } from '../../config/config';
import LogRocketFacade from '../../logger/log-rocket/LogRocketFacade';
import {
    isInstitutionLinkingInProgressAtom,
    isPartnerMembershipAtom,
} from '../state/HolisticAtoms';
import type { QuilttConnectorSource } from '../types/Quiltt';
import useInstitutionConnectDisclosure from './useInstitutionConnectDisclosure';
import useQuilttConnectorCallbacks from './useQuilttConnectorCallbacks';

const useConnectQuilttInstitution = (context?: QuilttConnectorSource) => {
    const user = useAuthedUser();

    const [isInstitutionLinkingInProgress, setIsInstitutionLinkingInProgress] =
        useAtom(isInstitutionLinkingInProgressAtom);
    const setIsPartnerMembership = useSetAtom(isPartnerMembershipAtom);

    const { onEvent, onExit, onExitError, onExitSuccess } =
        useQuilttConnectorCallbacks(context);

    const { open: openQuiltt } = useQuilttConnector(QUILTT_CONNECTOR_ID, {
        onEvent,
        onExit,
        onExitError,
        onExitSuccess,
    });

    useEffect(() => {
        setIsPartnerMembership(!!user.partnerMember);
    }, [!!user.partnerMember]);

    const {
        hasAcceptedInstitutionLinkDisclosures,
        setIsAwaitingDisclosuresAcceptance,
        setIsShowingInstitutionLinkDisclosures,
        showConnector,
    } = useInstitutionConnectDisclosure();

    useEffect(() => {
        showConnector && connectInstitution();
    }, [showConnector]);

    const connectInstitution = () => {
        if (isInstitutionLinkingInProgress) {
            return;
        }

        // In this case, we need to first have the member accept our linking disclosures
        // before we actually proceed with connecting to Quiltt and showing the linking UI
        if (!hasAcceptedInstitutionLinkDisclosures) {
            setIsAwaitingDisclosuresAcceptance(true);
            // Show the modal
            setIsShowingInstitutionLinkDisclosures(true);
            return;
        }

        setIsInstitutionLinkingInProgress(true);
        setIsAwaitingDisclosuresAcceptance(false);

        LogRocketFacade.log('Connecting, pausing view capture');
        LogRocketFacade.pauseViewCapture();

        openQuiltt();

        setIsInstitutionLinkingInProgress(false);
    };

    return {
        connectInstitution,
    };
};

export default useConnectQuilttInstitution;
